import { QueuedAudioFrame } from '@mimir/cutter/player/cutterCorePlayer';
import { AudioHandler } from './audioHandler';
import { AudioWorkletRequest, AudioWorkletResponse } from './audioWorkletProtocol';
import { AudioFrameV2 } from '@mimir/cutter/frameProvider/frameProvider';

import workletUrl from './audioWorkletProcessor.ts?worker&url';

interface Handlers {
  onStartPlaying: () => void,
  onCancelledPlayout: () => void,
}

export class WorkletAudioHandler implements AudioHandler {
  private bufferIdCounter : number = 0;
  private handlers : Record<string, Handlers> = { };
  private context = new AudioContext();
  private audioWorklet : AudioWorkletNode | null = null;
  private pendingRequests : AudioWorkletRequest[] = [];

  constructor() {
    /**
     *  Vite has special handling for worker source code, but it only matches source that
     *  starts with new "Worker(new URL". Since the AudioWorklet API uses addModule instead
     *  of new Worker we need to trick vite with this dummy Worker class. Try to beat this
     *  hack.
     */
    this.context.audioWorklet.addModule(workletUrl)
      .then(() => this.initialize());

    document.addEventListener('click', () => {
      if (this.context.state === 'suspended') {
        this.context.resume().then(() => {
        });
      }
    });
  }

  private initialize() {
    this.audioWorklet = new AudioWorkletNode(this.context, 'cutterAudioWorkletProcessor', { outputChannelCount: [2] });
    this.audioWorklet.connect(this.context.destination);
    this.audioWorklet.port.onmessage = (msg: MessageEvent<AudioWorkletResponse>) => {
      let handler = this.handlers[msg.data.bufferId];
      delete this.handlers[msg.data.bufferId];
      if (handler !== undefined) {
        switch (msg.data.type) {
          case 'start_playing_buffer': {
            handler.onStartPlaying();
            break;
          }
          case 'cancel_buffer': {
            handler.onCancelledPlayout();
            break;
          }
        }
      }
    };

    for (const request of this.pendingRequests.splice(0)) {
      this.sendRequest(request);
    }
    this.context.resume();
  }

  close(): void {
    this.context.close();
  }

  queueAudioFrame(audioFrame: QueuedAudioFrame): void {
    let samples = getFloatSamplesFromAudioFrame(audioFrame.audioFrame);
    const bufferId = this.bufferIdCounter.toString();

    if (audioFrame.audioFrame.channelCount !== 2) {
      samples = mixToStereo(samples, audioFrame.audioFrame.channelCount);
    }

    this.bufferIdCounter += 1;
    this.handlers[bufferId] = {
      onStartPlaying: audioFrame.onStartPlaying,
      onCancelledPlayout: audioFrame.onCancelledPlayout,
    }

    audioFrame.audioFrame.buffer.close();

    this.sendRequest({ type: 'frame', bufferId, samples });
  }

  startAudioPlayback(): void {
    this.sendRequest({ type: 'start_playback' });
  }

  stopAudioPlayback(clearQueue: boolean): void {
    this.sendRequest({ type: 'stop_playback', clearQueue });
  }

  sendRequest(req: AudioWorkletRequest) {
    if (this.audioWorklet !== null) {
      this.audioWorklet.port.postMessage(req);
    } else {
      this.pendingRequests.push(req);
    }
  }
}

function mixToStereo(samples: number[], channelCount: number): number[] {
  const outSamples : number[] = [];
  if (channelCount === 1) {
    for (const sample of samples) {
      outSamples.push(sample);
      outSamples.push(sample);
    }
  } else {
    const samplesPerChannel = samples.length / channelCount;
    for (let i = 0; i < samplesPerChannel; i++) {
      for (let j = 0; j < 2; j++) {
        let sample = 0;
        let srcChannelIndex = j;
        while (srcChannelIndex < channelCount) {
          sample += samples[(i * channelCount) + srcChannelIndex];
          srcChannelIndex += 2;
        }
        outSamples.push(sample);
      }
    }
  }
  return outSamples
}

function getFloatSamplesFromAudioFrame(frame: AudioFrameV2): number[] {
  switch (frame.format) {
    case 'pcm16': {
      let inSamples = frame.buffer.asReadonlyInt16Array();
      let outSamples : number[] = Array.from({ length: inSamples.length });
        // eslint-disable-next-line no-bitwise
      let factor = 1.0 / (1 << 15);
      for (let i = 0; i < inSamples.length; i++) {
        outSamples[i] = inSamples[i] * factor;
      }
      return outSamples;
    }
    case 'float32': {
      let inSamples = frame.buffer.asReadonlyFloat32Array();
      const outSamples : number[] = Array.from({ length: inSamples.length });
      for (let i = 0; i < inSamples.length; i++) {
        outSamples[i] = inSamples[i];
      }
      return outSamples;
    }
  }
}

